import React, { useState } from 'react';
import {
  Button, Upload, Table, Alert, Typography, Space, Tag, Steps, Statistic,
  Row, Col, Card, Popconfirm
} from 'antd';
import { staticApi } from '../../utils/staticApi';
import {
  UploadOutlined, CheckCircleOutlined, CloseCircleOutlined,
  FileTextOutlined, SendOutlined, ReloadOutlined, DownloadOutlined
} from '@ant-design/icons';
import type { UploadFile } from 'antd';
import { apiService } from '../../services/api';
import { atriumTokens } from '../../../../theme/theme';
import type { BulkUploadResponse, BulkPaymentPreviewRow, BulkConfirmResult } from '../../types';

const { Title } = Typography;

interface BulkPaymentUploadProps {
  condoId: string;
  onComplete?: (result: BulkConfirmResult) => void;
}

type Step = 'upload' | 'preview' | 'confirm' | 'done';

export const BulkPaymentUpload: React.FC<BulkPaymentUploadProps> = ({ condoId, onComplete }) => {
  const [step, setStep] = useState<Step>('upload');
  const [uploading, setUploading] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const [preview, setPreview] = useState<BulkUploadResponse | null>(null);
  const [result, setResult] = useState<BulkConfirmResult | null>(null);
  const [fileList, setFileList] = useState<UploadFile[]>([]);

  const handleUpload = async () => {
    if (!fileList.length) {
      staticApi.message.error('Please select a CSV file first');
      return;
    }
    const file = fileList[0].originFileObj as File;
    setUploading(true);
    try {
      const resp = await apiService.uploadBulkPayments(condoId, file);
      setPreview(resp);
      setStep('preview');
    } catch (err: any) {
      staticApi.message.error(err.message || 'Upload failed');
    } finally {
      setUploading(false);
    }
  };

  const handleConfirm = async () => {
    if (!preview) return;
    setConfirming(true);
    try {
      const confirmResult = await apiService.confirmBulkPayments(preview.jobId, preview.validRows);
      setResult(confirmResult);
      setStep('done');
      onComplete?.(confirmResult);
    } catch (err: any) {
      staticApi.message.error(err.message || 'Confirmation failed');
    } finally {
      setConfirming(false);
    }
  };

  const handleReset = () => {
    setStep('upload');
    setPreview(null);
    setResult(null);
    setFileList([]);
  };

  const downloadTemplate = () => {
    const csv = `unit_number,amount,payment_date,payment_method,reference_number,notes\n101,450.00,2026-04-16,cheque,CHQ-001,April Maintenance Fee\n102,450.00,2026-04-16,etransfer,,April Maintenance Fee\n103,450.00,2026-04-16,cash,,\n`;
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'bulk_payment_template.csv';
    a.click();
    URL.revokeObjectURL(url);
  };

  // Combine valid and invalid rows for preview display
  const allPreviewRows: (BulkPaymentPreviewRow & { key: number })[] = preview
    ? [
        ...preview.validRows.map(r => ({ ...r, key: r.row })),
        ...preview.invalidRows.map(r => ({ ...r, key: r.row + 100000 })),
      ].sort((a, b) => a.row - b.row)
    : [];

  const previewColumns = [
    { title: 'Row', dataIndex: 'row', key: 'row', width: 60 },
    { title: 'Unit', dataIndex: 'unitNumber', key: 'unitNumber', width: 80 },
    { title: 'Amount', dataIndex: 'amount', key: 'amount', width: 100,
      render: (v: string) => v ? `$${parseFloat(v).toLocaleString('en-CA', { minimumFractionDigits: 2 })}` : '—' },
    { title: 'Payment Date', dataIndex: 'paymentDate', key: 'paymentDate', width: 120 },
    { title: 'Method', dataIndex: 'paymentMethod', key: 'paymentMethod', width: 100,
      render: (v: string) => v ? <Tag>{v}</Tag> : '—' },
    { title: 'Reference', dataIndex: 'referenceNumber', key: 'referenceNumber',
      render: (v: string) => v || '—' },
    { title: 'Notes', dataIndex: 'notes', key: 'notes',
      render: (v: string) => v || '—' },
    {
      title: 'Status',
      key: 'status',
      width: 160,
      render: (_: any, row: BulkPaymentPreviewRow) =>
        row.errors && row.errors.length > 0 ? (
          <Tag color="red" icon={<CloseCircleOutlined />}>{row.errors[0]}</Tag>
        ) : (
          <Tag color="green" icon={<CheckCircleOutlined />}>Valid</Tag>
        ),
    },
  ];

  const steps = [
    { title: 'Upload', description: 'Select CSV' },
    { title: 'Preview', description: 'Review rows' },
    { title: 'Confirm', description: 'Process payments' },
    { title: 'Done', description: 'Results' },
  ];
  const currentStep = ['upload', 'preview', 'confirm', 'done'].indexOf(step);

  return (
    <div>
      <Steps items={steps} current={currentStep} style={{ marginBottom: 32 }} size="small" />

      {step === 'upload' && (
        <div>
          <Alert
            type="info"
            showIcon
            icon={<FileTextOutlined />}
            message="Bulk Payment Upload — CSV Format"
            description={
              <div>
                <p style={{ margin: '4px 0' }}>Required columns: <strong>unit_number, amount, payment_date, payment_method</strong></p>
                <p style={{ margin: '4px 0' }}>Optional columns: reference_number, notes</p>
                <p style={{ margin: '4px 0' }}>payment_date format: YYYY-MM-DD (e.g., 2026-04-16)</p>
                <p style={{ margin: '4px 0' }}>amount format: decimal number (e.g., 450.00)</p>
                <p style={{ margin: '4px 0' }}>payment_method: <strong>card</strong> | <strong>etransfer</strong> | <strong>cheque</strong> | <strong>cash</strong></p>
              </div>
            }
            style={{ marginBottom: 20, borderRadius: 8 }}
          />
          <Space direction="vertical" style={{ width: '100%' }} size={16}>
            <Button
              icon={<DownloadOutlined />}
              onClick={downloadTemplate}
              style={{ borderRadius: 8 }}
            >
              Download CSV Template
            </Button>
            <Upload
              accept=".csv"
              maxCount={1}
              fileList={fileList}
              beforeUpload={() => false}
              onChange={({ fileList: fl }) => setFileList(fl)}
            >
              <Button icon={<UploadOutlined />} size="large" style={{ borderRadius: 8, width: 280 }}>
                Select CSV File
              </Button>
            </Upload>
            {fileList.length > 0 && (
              <Button
                type="primary"
                size="large"
                loading={uploading}
                onClick={handleUpload}
                style={{ borderRadius: 8, paddingInline: 32 }}
              >
                {uploading ? 'Uploading...' : 'Upload & Preview'}
              </Button>
            )}
          </Space>
        </div>
      )}

      {step === 'preview' && preview && (
        <div>
          <Row gutter={16} style={{ marginBottom: 20 }}>
            <Col span={8}>
              <Card size="small" style={{ borderRadius: 8, textAlign: 'center' }}>
                <Statistic title="Total Rows" value={preview.totalRows} />
              </Card>
            </Col>
            <Col span={8}>
              <Card size="small" style={{ borderRadius: atriumTokens.radiusCard, textAlign: 'center', background: atriumTokens.okS }}>
                <Statistic title="Valid" value={preview.validCount} valueStyle={{ color: atriumTokens.ok }} prefix={<CheckCircleOutlined />} />
              </Card>
            </Col>
            <Col span={8}>
              <Card size="small" style={{ borderRadius: atriumTokens.radiusCard, textAlign: 'center', background: preview.invalidCount > 0 ? atriumTokens.redS : undefined }}>
                <Statistic title="Invalid" value={preview.invalidCount} valueStyle={{ color: preview.invalidCount > 0 ? atriumTokens.red : atriumTokens.mut }} prefix={<CloseCircleOutlined />} />
              </Card>
            </Col>
          </Row>

          {preview.invalidCount > 0 && (
            <Alert
              type="warning"
              message={`${preview.invalidCount} row(s) have errors and will be skipped. Only valid rows will be processed.`}
              showIcon
              style={{ marginBottom: 16, borderRadius: 8 }}
            />
          )}

          <Table
            dataSource={allPreviewRows}
            columns={previewColumns}
            pagination={{ pageSize: 10 }}
            size="small"
            scroll={{ x: 900 }}
            style={{ marginBottom: 20 }}
            rowClassName={(row: BulkPaymentPreviewRow) =>
              row.errors && row.errors.length > 0 ? 'bulk-row-invalid' : ''
            }
          />

          <Space>
            <Button icon={<ReloadOutlined />} onClick={handleReset} style={{ borderRadius: 8 }}>
              Start Over
            </Button>
            {preview.validCount > 0 && (
              <Popconfirm
                title={`Process ${preview.validCount} payment(s)?`}
                description={`This will create ${preview.validCount} payment records. This action cannot be undone.`}
                onConfirm={handleConfirm}
                okText="Confirm"
                cancelText="Cancel"
                okButtonProps={{ danger: false }}
              >
                <Button
                  type="primary"
                  icon={<SendOutlined />}
                  loading={confirming}
                  size="large"
                  style={{ borderRadius: 8, paddingInline: 32 }}
                >
                  Process {preview.validCount} Payment{preview.validCount !== 1 ? 's' : ''}
                </Button>
              </Popconfirm>
            )}
          </Space>
        </div>
      )}

      {step === 'done' && result && (
        <div style={{ textAlign: 'center' }}>
          <div style={{ fontSize: 64, marginBottom: 16 }}>
            {result.failed === 0 ? '✅' : '⚠️'}
          </div>
          <Title level={3}>
            {result.failed === 0 ? 'Bulk Payment Complete!' : 'Bulk Payment Finished with Errors'}
          </Title>
          <Row gutter={24} justify="center" style={{ marginBottom: 24 }}>
            <Col span={8}>
              <Card size="small" style={{ borderRadius: atriumTokens.radiusCard, background: atriumTokens.okS }}>
                <Statistic title="Processed" value={result.processed} valueStyle={{ color: atriumTokens.ok }} prefix={<CheckCircleOutlined />} />
              </Card>
            </Col>
            <Col span={8}>
              <Card size="small" style={{ borderRadius: atriumTokens.radiusCard, background: result.failed > 0 ? atriumTokens.redS : undefined }}>
                <Statistic title="Failed" value={result.failed} valueStyle={{ color: result.failed > 0 ? atriumTokens.red : atriumTokens.mut }} prefix={<CloseCircleOutlined />} />
              </Card>
            </Col>
          </Row>
          {result.failedRows && result.failedRows.length > 0 && (
            <Alert
              type="error"
              message={`${result.failed} row(s) failed to process. Check the failed row details.`}
              showIcon
              style={{ marginBottom: 16, borderRadius: 8, maxWidth: 500, margin: '0 auto 16px' }}
            />
          )}
          <Button type="primary" onClick={handleReset} icon={<ReloadOutlined />} style={{ borderRadius: 8 }}>
            Upload Another File
          </Button>
        </div>
      )}
    </div>
  );
};
